import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { addFavouriteRestaurant, profile as PROFILE_QUERY } from "../../apollo/server";
import UserContext from "../../context/User";
import ConfigurationContext from "../../context/Configuration";
import { resizedImage, resizedSrcSet } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../utils/designTokens";

const ADD_FAVOURITE = gql`${addFavouriteRestaurant}`;
const PROFILE = gql`${PROFILE_QUERY}`;

/** "24h", "48h", "90 min". */
export const noticeLabel = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return m >= 60 ? `${Math.ceil(m / 60)}h` : `${m} min`;
};

const chip = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  height: 28,
  px: 1.25,
  borderRadius: "999px",
  fontFamily: FONT_BODY,
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  boxShadow: "0 1px 3px rgba(26,25,24,0.12)",
  "& svg": { fontSize: 15 },
};

function InfoBox({ children }) {
  return (
    <Box sx={{ flex: "1 1 0", minWidth: 0, px: 1.5, py: 1, borderRadius: "12px", bgcolor: C.surface, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.ink, lineHeight: 1.35 }}>
      {children}
    </Box>
  );
}

/** "Serves 10–40 guests", from the items' serving sizes and the caterer's limit. */
export function servesLabel(r) {
  const sizes = r.cateringServes || [];
  const min = sizes.length ? sizes[0] : null;
  const max = r.catering?.maxGuests || (sizes.length ? sizes[sizes.length - 1] : null);
  if (min && max && max > min) return `Serves ${min}–${max} guests`;
  if (max && !min) return `Serves up to ${max} guests`;
  if (min) return `Serves ${min}+ guests`;
  return "Serves groups";
}

/**
 * One caterer. `deal` is its catering deal in a few words ("15% off $50+"),
 * `fits` is false when the planned time is sooner than its notice; `onOpen`
 * opens its catering menu.
 */
function CatererCard({ r, deal, guests, fits, leadMinutes, onOpen, onMessage }) {
  const { profile } = useContext(UserContext);
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const saved = Boolean(profile?.favourite?.includes(r._id));
  const [fav, setFav] = useState(saved);
  useEffect(() => setFav(saved), [saved]);
  const [toggleFavourite] = useMutation(ADD_FAVOURITE, {
    refetchQueries: [{ query: PROFILE }],
    onError: () => {
      setFav(saved);
      onMessage?.({ type: "error", message: "Couldn't update your favourites. Please try again." });
    },
  });
  const onHeart = () => {
    if (!profile) {
      onMessage?.({ type: "error", message: "Please log in to add favorites" });
      return;
    }
    setFav((v) => !v);
    toggleFavourite({ variables: { id: r._id } });
  };

  const image = r.image || r.logo;
  const logo = r.image && r.logo && r.logo !== r.image ? r.logo : null;
  const miles = r.distance != null ? `${(r.distance * 0.621371).toFixed(1)} mi` : null;
  const count = r.reviewData?.total || r.reviewData?.reviews?.length || 0;
  const avg = Number(r.reviewData?.ratings) || 0;
  const cuisineLine = [(r.cuisines || []).slice(0, 2).join(" · "), miles].filter(Boolean).join(" · ");

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: "20px",
        overflow: "hidden",
        transition: "box-shadow .15s ease, border-color .15s ease",
        "&:hover": { borderColor: C.borderStrong, boxShadow: "0 6px 18px rgba(26,25,24,0.06)" },
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 9", bgcolor: C.surface2 }}>
        {image && (
          <Box
            component="img"
            src={resizedImage(image, 800)}
            srcSet={resizedSrcSet(image, [480, 800])}
            sizes="(max-width: 600px) 100vw, 420px"
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        {leadMinutes > 0 && (
          <Box component="span" sx={{ ...chip, position: "absolute", top: 12, left: 12, bgcolor: C.bg, color: C.ink, pointerEvents: "none" }}>
            <AccessTimeRoundedIcon aria-hidden="true" />
            Order {noticeLabel(leadMinutes)} ahead
          </Box>
        )}
        {deal && (
          <Box component="span" sx={{ ...chip, position: "absolute", left: 12, bottom: 12, bgcolor: C.green, color: "#FFFFFF", pointerEvents: "none" }}>
            <LocalOfferRoundedIcon aria-hidden="true" />
            {deal}
          </Box>
        )}
        {logo && (
          <Box
            component="img"
            src={resizedImage(logo, 128)}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{ position: "absolute", right: 12, bottom: 12, width: 44, height: 44, boxSizing: "border-box", borderRadius: "12px", border: "2px solid #FFFFFF", bgcolor: C.bg, objectFit: "cover", boxShadow: "0 2px 8px rgba(26,25,24,0.20)", pointerEvents: "none" }}
          />
        )}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={onHeart}
        aria-pressed={fav}
        aria-label={fav ? `Remove ${r.name} from favourites` : `Save ${r.name} to favourites`}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1,
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: C.bg,
          color: fav ? C.red : C.ink,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(26,25,24,0.16)",
          "&:active": { transform: "scale(0.94)" },
        }}
      >
        {fav ? <FavoriteRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} /> : <FavoriteBorderRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} />}
      </Box>

      <Box sx={{ p: { xs: 2, sm: "18px" }, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box component="h3" sx={{ m: 0, flex: 1, minWidth: 0, fontFamily: FONT_BODY, fontSize: 18, fontWeight: 700, lineHeight: 1.3, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {r.name}
          </Box>
          {count ? (
            <Box component="span" aria-label={`Rated ${avg.toFixed(1)} from ${count} reviews`} sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.25, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.ink }}>
              <StarRoundedIcon aria-hidden="true" sx={{ fontSize: 17, color: C.goldAccent }} />
              {avg.toFixed(1)}
              <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>({count})</Box>
            </Box>
          ) : (
            <Box component="span" sx={{ flexShrink: 0, px: 1, height: 22, display: "inline-flex", alignItems: "center", borderRadius: "999px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700 }}>
              New
            </Box>
          )}
        </Box>
        {cuisineLine && (
          <Box sx={{ mt: -0.75, fontFamily: FONT_BODY, fontSize: 14, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{cuisineLine}</Box>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          <InfoBox>{servesLabel(r)}</InfoBox>
          <InfoBox>{r.cateringFromPrice != null ? `Trays from ${currency}${Number(r.cateringFromPrice).toFixed(2)}` : "See the menu for prices"}</InfoBox>
        </Box>
        {/* One row on every card: the label wraps, the button keeps its size. */}
        <Box sx={{ mt: "auto", pt: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          {fits ? (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, lineHeight: 1.3, color: C.green }}>
              <CheckCircleRoundedIcon aria-hidden="true" sx={{ fontSize: 18, flexShrink: 0 }} />
              Available for {guests} guests
            </Box>
          ) : (
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0, px: 1, py: 0.5, borderRadius: "14px", bgcolor: C.goldSoft, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, lineHeight: 1.3, color: C.gold }}>
              <ScheduleRoundedIcon aria-hidden="true" sx={{ fontSize: 17, flexShrink: 0 }} />
              Needs {noticeLabel(leadMinutes)} notice
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={() => onOpen(r)}
            aria-label={`View ${r.name}'s catering menu`}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              flexShrink: 0,
              height: 44,
              px: 2,
              borderRadius: RADIUS.pill,
              bgcolor: C.red,
              color: "#FFFFFF",
              fontFamily: FONT_BODY,
              fontSize: 14,
              fontWeight: 700,
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: C.redHover },
            }}
          >
            View catering menu
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(CatererCard);
